import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Card, Modal, Row, Col } from "react-bootstrap";
import * as validator from "../../../../helpers/validator";
import { trimFieldsData } from "../../../../utils";
import { DataTable } from "../../../common";
import { ButtonView, Input, Select, Pagination } from "../../../controls";

const initialRoomData = {
  name: "",
  location: "",
};

const RoomsTab = ({
  roomList,
  addRoom,
  editRoom,
  archiveRoom,
  locationList,
  roomPagination,
  listRooms,
  roomManagementPage,
  setRoomManagementPage,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [roomData, setRoomData] = useState(initialRoomData);
  const [isEdit, setIsEdit] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedRoomId, setSelectedRoomId] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState(null);
  const [pagination, setPagination] = useState({ page: roomManagementPage || 1, limit: 5 });

  useEffect(() => {
    if (roomManagementPage) {
      setPagination((prev) => ({ ...prev, page: roomManagementPage }));
    }
  }, [roomManagementPage]);

  const handlePageChange = (page) => {
    setPagination({ ...pagination, page });
    if (setRoomManagementPage) {
      setRoomManagementPage(page);
    }
    listRooms({ page, limit: pagination.limit });
  };



  const locationOptions = (locationList || []).map((loc) => ({
    value: loc._id,
    label: loc.name,
  }));

  const selectedLocation = locationOptions.find((o) => {
    const locId = roomData.location && typeof roomData.location === "object" ? roomData.location._id : roomData.location;
    return o.value === locId;
  }) || null;

  const columns = [
    {
      name: "Room Name",
      selector: "name",
      sortable: true,
      grow: 4,
    },
    {
      name: "Location / Floor",
      selector: "location.name",
      sortable: true,
      grow: 4,
      cell: (row) => row.location ? (row.location.name || row.location) : "N/A",
    },
    {
      name: "Option",
      center: true,
      minWidth: "150px",
      grow: 2,
      cell: (row) => (
        <div className="d-flex">
          <div className="me-2">
            <ButtonView
              onClick={() => {
                setIsEdit(true);
                setRoomData({
                  name: row.name,
                  location: row.location ? (row.location._id || row.location) : "",
                });
                setSelectedRoomId(row._id);
                setShowModal(true);
              }}
              title={"Edit"}
              size="small"
              styles="secondary"
            />
          </div>
          <ButtonView
            onClick={() => {
              setRoomToDelete(row);
              setShowDeleteModal(true);
            }}
            title={"Remove"}
            size="small"
            styles="primal"
          />
        </div>
      ),
    },
  ];

  const handleSubmit = () => {
    const rules = { required: ["name", "location"] };
    const { errors: validationErrors } = validator.validate(rules, {
      ...trimFieldsData(roomData),
    });

    setErrors(validationErrors);

    if (!Object.keys(validationErrors).length) {
      if (isEdit) {
        editRoom({
          roomId: selectedRoomId,
          ...roomData,
        }, pagination);
      } else {
        addRoom(roomData, pagination);
      }
      handleClose();
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setRoomData(initialRoomData);
    setIsEdit(false);
    setErrors({});
    setSelectedRoomId("");
  };

  return (
    <div className="rooms-management-tab mt-3">
      <Card>
        <Card.Body>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <Card.Title className="m-0">Rooms List</Card.Title>
            <div>
              <ButtonView
                onClick={() => setShowModal(true)}
                title={"Add Room"}
              />
            </div>
          </Card.Header>
          <DataTable
            data={roomList}
            columns={columns}
            pagination={false}
          />

          {roomPagination && roomPagination.totalDocs > 0 && (
            <div className="margin_x mt-2 d-flex justify-content-end">
              <Pagination
                rowsPerPage={roomPagination.limit}
                rowCount={roomPagination.totalDocs}
                onChangePage={handlePageChange}
                currentPage={roomPagination.page}
              />
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleClose} aria-labelledby="room-modal">
        <Modal.Header>
          <Modal.Title id="room-modal">
            {isEdit ? "Edit Room" : "Add Room"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="room-form">
            <Row className="mb-2">
              <Col xs={12} className="mb-3">
                <Input
                  name="name"
                  label="Room Name"
                  value={roomData.name}
                  type="text"
                  placeholder="e.g., Conference Room A"
                  onChange={(e) =>
                    setRoomData({ ...roomData, name: e.target.value })
                  }
                  size="fluid"
                  error={errors["name"]}
                  isrequired={true}
                />
              </Col>
              <Col xs={12} className="mb-3">
                <Select
                  label="Location"
                  value={selectedLocation}
                  options={locationOptions}
                  onChange={(selectedOption) =>
                    setRoomData({ ...roomData, location: selectedOption ? selectedOption.value : "" })
                  }
                  isrequired={true}
                  error={errors["location"]}
                />
              </Col>
              <Col xs={12} className="d-flex justify-content-end mt-3">
                <div className="me-2">
                  <ButtonView
                    onClick={handleClose}
                    title="Cancel"
                    styles="primal"
                  />
                </div>
                <ButtonView
                  onClick={handleSubmit}
                  title={isEdit ? "Save" : "Add"}
                  styles="secondary"
                />
              </Col>
            </Row>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title>Remove Room</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to remove this room?
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
            Close
          </button>
          <button className="btn btn-danger" onClick={() => {
            if (roomToDelete) {
              archiveRoom({ roomId: roomToDelete._id }, pagination);
              setShowDeleteModal(false);
              setRoomToDelete(null);
            }
          }}>
            Confirm Remove
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

RoomsTab.propTypes = {
  roomList: PropTypes.array.isRequired,
  addRoom: PropTypes.func.isRequired,
  editRoom: PropTypes.func.isRequired,
  archiveRoom: PropTypes.func.isRequired,
  locationList: PropTypes.array.isRequired,
  roomPagination: PropTypes.object,
  listRooms: PropTypes.func.isRequired,
  roomManagementPage: PropTypes.number,
  setRoomManagementPage: PropTypes.func,
};

export default RoomsTab;
